// sprites.ts ↔ the SDL sprite overlay stack: game/sdl/sdlspritemgr.cpp
// (SdlSpriteManager), game/sdl/sdlanimsprite.cpp (SdlAnimSprite — the unit
// HILIGHT pulse: captures the unit's current animation frame and re-blits it
// scaled, centered under the finger), game/sdl/sdlselectionsprite.cpp
// (SdlSelectionSprite — the two-finger drag-select rect + grabbies).
//
// The C++ stack draws onto the GPU back texture AFTER the form managers
// (Display::FrameComplete, display.cpp:291-307); here the session's
// displayFrameComplete seam calls SpriteManager.DrawSprites over the shared
// back dib. The C++ thread plumbing (CriticalSection around capture vs draw,
// the base::MessageHandler scale-animation timer) collapses: the JS host is
// single-threaded, and SdlAnimSprite::SetScaleAnimation is short-circuited in
// the shipped build (`#if 1 SetScale(nScaleEnd); return;`).

import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { Rect } from './gobdraw.ts';
import {
  GobDrawContext,
  gobAnimationHash,
  gobAnimationBounds,
  gobDrawAnimation,
} from './gobdraw.ts';
import { BaseSelectionSprite } from '../ui/fingerhandler.ts';
import type { GetColor } from '../gfx/palette.ts';

export interface SpriteLike {
  Draw(pbm: CpuDibBitmap, siz: { cx: number; cy: number }, rcClip: Rect | null): void;
  /** the sprite's current on-screen bounds (playfield px, pre-clip) for the damage-
   *  driven playfield repaint — null when it draws nothing. The sprites draw straight
   *  onto the back buffer AFTER the form managers (displayFrameComplete), so the
   *  playfield beneath every region they touched last frame / will touch this frame
   *  must be invalidated each frame (GameRenderer.frameStartInvalidate →
   *  SpriteManager.CollectDamage). May return a REUSED rect owned by the sprite —
   *  consume synchronously or copy to retain. */
  DamageRect(): Rect | null;
}

/** The WebGL2 surface's render-time hooks (gfx/gl/glsurface.ts). When the
 *  target is GL-backed (no CPU rgba), the two direct-pixel paths below
 *  dispatch to these; on the CPU reference surface they are undefined and
 *  the original byte-exact loops run unchanged. */
interface GlSpriteTarget {
  glScaledBlit?(
    src: { cx: number; cy: number; rgba: Uint8Array },
    xDst: number,
    yDst: number,
    cxDst: number,
    cyDst: number,
    rcClip: Rect | null,
  ): void;
  glDrawLineClipped?(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    clr: { r: number; g: number; b: number },
    rcClip: Rect,
  ): void;
}

// the capture surface gobDrawAnimation/gobAnimationHash consume
interface CaptureGob {
  GetType(): number;
  GetId(): number;
  GetFlags(): number;
  GetSide(): number;
  m_wx: number;
  m_wy: number;
  m_wfUnit: number;
  m_ani: { GetStrip(): number; GetFrame(): number };
  m_puntc: {
    szAniName: string;
    wrcUIBounds: { left: number; top: number; right: number; bottom: number };
    GetArmorStrength(): number;
  };
}

/** game/sdl/sdlspritemgr.cpp SdlSpriteManager — a fixed 16-slot overlay list.
 *  Add dedups; Remove compacts; Update just marks dirty (the TS host repaints
 *  every frame anyway); DrawSprites runs each sprite TWICE, clipped to the two
 *  playfield rects (left-of-minimap, above-minimap — Game::
 *  SizeAndPositionEverything, game.cpp:2104-2119). */
export class SpriteManager {
  private apspr_: SpriteLike[] = [];
  private fSpriteDirty_ = false;
  private rcClip1_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  private rcClip2_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };

  // sdlspritemgr.cpp:19-37
  Add(pspr: SpriteLike): void {
    if (!this.apspr_.includes(pspr) && this.apspr_.length < 15) {
      this.apspr_.push(pspr); // ARRAYSIZE(apspr_) - 1 = 15 cap
    }
    this.fSpriteDirty_ = true;
  }

  // sdlspritemgr.cpp:39-56
  Remove(pspr: SpriteLike): void {
    const i = this.apspr_.indexOf(pspr);
    if (i >= 0) this.apspr_.splice(i, 1);
    // a removed sprite's last-drawn area still needs one repaint beneath it
    const rc = this.lastDamage_.get(pspr);
    if (rc !== undefined) {
      this.orphanDamage_.push(rc);
      this.lastDamage_.delete(pspr);
    }
    this.fSpriteDirty_ = true;
  }

  // sdlspritemgr.cpp:57-59
  Update(_pspr: SpriteLike): void {
    this.fSpriteDirty_ = true;
  }

  // sdlspritemgr.cpp:61-79 — each sprite draws once per clip rect
  DrawSprites(pbm: CpuDibBitmap, siz: { cx: number; cy: number }): void {
    if (this.apspr_.length === 0) return;
    for (const pspr of this.apspr_) {
      pspr.Draw(pbm, siz, this.rcClip1_);
      pspr.Draw(pbm, siz, this.rcClip2_);
      // record the footprint AS DRAWN — the eraser bookkeeping must track what
      // actually landed on the back buffer, not what CollectDamage saw at frame
      // start (a sprite can move/recapture between the two; a stale record here
      // would strand its pixels if it is Remove()d before the next frame start).
      // COPIED into an owned rect: DamageRect may return the sprite's reused one.
      const rc = pspr.DamageRect();
      if (rc === null) {
        this.lastDamage_.delete(pspr);
      } else {
        let e = this.lastDamage_.get(pspr);
        if (e === undefined) {
          e = { left: 0, top: 0, right: 0, bottom: 0 };
          this.lastDamage_.set(pspr, e);
        }
        e.left = rc.left;
        e.top = rc.top;
        e.right = rc.right;
        e.bottom = rc.bottom;
      }
    }
  }

  // sdlspritemgr.cpp:81-92
  SetClipRects(prc1: Rect, prc2: Rect): void {
    this.rcClip1_ = { ...prc1 };
    this.rcClip2_ = { ...prc2 };
  }

  private readonly lastDamage_ = new Map<SpriteLike, Rect>();
  private readonly orphanDamage_: Rect[] = [];

  /** Damage-driven playfield repaint (GameRenderer.frameStartInvalidate): report every
   *  region the overlay sprites drew last frame (lastDamage_, recorded at DrawSprites
   *  time) or will draw this frame, so the playfield beneath them repaints before
   *  DrawSprites re-draws them on top. Under the SDL-era full-repaint-per-frame this
   *  was implicit; with scoped invalidation the sprites must damage their own
   *  footprints or they smear when moved/removed.
   *
   *  Gated on fSpriteDirty_ (Add/Remove/Update/recapture all raise it): an UNCHANGED
   *  sprite re-draws byte-identical pixels over its own previous draw, so it needs no
   *  damage — only a change (move/scale/show/content) does. Conservatively damages
   *  drawn+new bounds of EVERY live sprite on a dirty frame (≤15 sprites, interaction-
   *  transient — precision isn't worth per-sprite dirty bits). lastDamage_ is NOT
   *  updated here — DrawSprites owns it (the record must be what actually landed). */
  CollectDamage(cb: (rc: Rect) => void): void {
    for (const rc of this.orphanDamage_) cb(rc);
    this.orphanDamage_.length = 0;
    if (!this.fSpriteDirty_) return;
    this.fSpriteDirty_ = false;
    for (const pspr of this.apspr_) {
      const rcNew = pspr.DamageRect();
      const rcOld = this.lastDamage_.get(pspr);
      if (rcOld !== undefined) cb(rcOld);
      if (rcNew !== null) cb(rcNew);
    }
  }

  /** The view scrolled by (dx,dy): the incremental scroll blit shifts previously-drawn
   *  content — including sprite pixels — by exactly (dx,dy) (FormMgr.Scroll's
   *  accumulate feeds ScrollBits' src/dst rects with that convention). Damage each
   *  drawn footprint at its pre-shift position, SHIFT the record in place to where
   *  the blit moved the pixels, and damage that too; orphan rects of already-removed
   *  sprites shift the same way. Compounding across multiple scrolls before a paint
   *  is safe: UpdateMap.Scroll shifts the accumulated invalid flags with the content.
   *  No-op when nothing has drawn. */
  ScrollDamage(dx: number, dy: number, cb: (rc: Rect) => void): void {
    const shift = (rc: Rect): void => {
      cb(rc); // where the pixels were
      rc.left += dx;
      rc.right += dx;
      rc.top += dy;
      rc.bottom += dy;
      cb(rc); // where the blit moved them
    };
    for (const rc of this.orphanDamage_) shift(rc);
    this.orphanDamage_.length = 0;
    for (const rc of this.lastDamage_.values()) shift(rc);
  }

  /** spritemgr.h:16 / sdlspritemgr.h CreateAnimSprite. */
  CreateAnimSprite(ctx: GobDrawContext): AnimSprite {
    return new AnimSprite(this, ctx);
  }

  /** sdlspritemgr.h CreateSelectionSprite. */
  CreateSelectionSprite(): RenderSelectionSprite {
    return new RenderSelectionSprite(this);
  }

  count(): number {
    return this.apspr_.length;
  } // test reach
}

const clip = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

/** game/sdl/sdlanimsprite.cpp SdlAnimSprite — the hilight sprite. */
export class AnimSprite implements SpriteLike {
  // sdlanimsprite.cpp:8-20 field init
  private hash_ = 0;
  private cx_ = 0;
  private cy_ = 0;
  private xOrigin_ = 0;
  private yOrigin_ = 0;
  private pbm_: CpuDibBitmap | null = null;
  private nScale_ = 1.0;
  private x_ = 0;
  private y_ = 0;
  private fVisible_ = true;
  private rcUI_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  private rcBase_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  private readonly psprm_: SpriteManager;
  private readonly ctx: GobDrawContext;

  constructor(psprm: SpriteManager, ctx: GobDrawContext) {
    this.psprm_ = psprm;
    this.ctx = ctx;
  }

  // sdlanimsprite.cpp:22-30 (~SdlAnimSprite) — delete m_panispr's reach
  Dispose(): void {
    this.psprm_.Remove(this);
    this.pbm_ = null;
  }

  // sdlanimsprite.cpp:32-60 — recapture when the animation hash moves
  CaptureFrame(pgob: unknown): void {
    const punt = pgob as CaptureGob;
    const hash = gobAnimationHash(punt as never);
    if (hash === this.hash_) return;
    this.hash_ = hash;

    // rcUI_ = UI bounds in PIXELS (Rect::FromWorldRect)
    const c = this.ctx.coords;
    const wrc = punt.m_puntc.wrcUIBounds;
    this.rcUI_ = {
      left: c.PcFromWc(wrc.left),
      top: c.PcFromWc(wrc.top),
      right: c.PcFromWc(wrc.right),
      bottom: c.PcFromWc(wrc.bottom),
    };

    if (!this.CreateSurface(punt)) {
      // degenerate new bounds: nothing to draw this capture, but the PREVIOUS
      // capture's pixels are still on screen — raise the dirty flag so the next
      // CollectDamage erases the old footprint (DamageRect is null now, so only
      // the drawn record gets damaged). The SDL-era full repaint hid this path.
      this.psprm_.Update(this);
      return;
    }
    this.psprm_.Add(this);
  }

  // sdlanimsprite.cpp:62-98
  private CreateSurface(punt: CaptureGob): boolean {
    const rcAni = gobAnimationBounds(this.ctx, punt as never, false);
    this.cx_ = (rcAni.right - rcAni.left + 1) & ~1;
    this.cy_ = rcAni.bottom - rcAni.top;
    this.rcBase_ = gobAnimationBounds(this.ctx, punt as never, true);
    this.xOrigin_ = rcAni.left; // negative: identifies the origin
    this.yOrigin_ = rcAni.top;
    if (this.cx_ <= 0 || this.cy_ <= 0) return false;
    // the C++ frees the previous frame's texture FIRST (the 128MB-heap leak
    // note); here the old dib is simply dropped
    this.pbm_ = new CpuDibBitmap(this.cx_, this.cy_);
    gobDrawAnimation(this.ctx, punt as never, this.pbm_, -this.xOrigin_, -this.yOrigin_);
    return true;
  }

  /** current on-screen dest rect — exactly what Draw blits (Draw derives from THIS,
   *  so the two can't drift). Returns a reused buffer; copy to retain. */
  private readonly rcDamage_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  DamageRect(): Rect | null {
    if (!this.fVisible_ || this.pbm_ === null) return null;
    const rcBounds = this.GetBounds();
    if (rcBounds.right <= rcBounds.left || rcBounds.bottom <= rcBounds.top) return null;
    const rc = this.rcDamage_;
    rc.left = this.x_ + rcBounds.left;
    rc.top = this.y_ + rcBounds.top;
    rc.right = this.x_ + rcBounds.right;
    rc.bottom = this.y_ + rcBounds.bottom;
    return rc;
  }

  // sdlanimsprite.cpp:100-126 — scaled blit at x_/y_ + scaled bounds; SDL
  // scales the texture over the dest rect (nearest sampling here — the CPU
  // oracle's integer-aligned policy)
  Draw(pbm: CpuDibBitmap, _siz: { cx: number; cy: number }, rcClip: Rect | null): void {
    const rcDst = this.DamageRect();
    if (rcDst === null) return;
    const xDst = rcDst.left;
    const yDst = rcDst.top;
    const cxDst = rcDst.right - rcDst.left;
    const cyDst = rcDst.bottom - rcDst.top;
    const src = this.pbm_!;
    const glScaled = (pbm as unknown as GlSpriteTarget).glScaledBlit;
    if (glScaled !== undefined) {
      glScaled.call(pbm, src, xDst, yDst, cxDst, cyDst, rcClip);
      return;
    }
    const d = pbm.rgba;
    for (let y = 0; y < cyDst; y++) {
      const dy = yDst + y;
      if (dy < 0 || dy >= pbm.cy) continue;
      if (rcClip !== null && (dy < rcClip.top || dy >= rcClip.bottom)) continue;
      const sy = clip(Math.trunc((y * src.cy) / cyDst), 0, src.cy - 1);
      for (let x = 0; x < cxDst; x++) {
        const dx = xDst + x;
        if (dx < 0 || dx >= pbm.cx) continue;
        if (rcClip !== null && (dx < rcClip.left || dx >= rcClip.right)) continue;
        const sx = clip(Math.trunc((x * src.cx) / cxDst), 0, src.cx - 1);
        const si = (sy * src.cx + sx) * 4;
        const sa = src.rgba[si + 3];
        if (sa === 0) continue; // SDL_BLENDMODE_BLEND, binary-alpha art
        const di = (dy * pbm.cx + dx) * 4;
        d[di] = src.rgba[si];
        d[di + 1] = src.rgba[si + 1];
        d[di + 2] = src.rgba[si + 2];
        d[di + 3] = 255;
      }
    }
  }

  // sdlanimsprite.cpp:128-156 — scale the UI rect about its center; center the
  // BASE height inside it; offset so the base stays put. C++ does this in
  // float and lands in int fields — Math.trunc mirrors the implicit casts.
  GetBounds(): Rect {
    const rcUI = this.rcUI_;
    const cxUI = rcUI.right - rcUI.left;
    const cyUI = rcUI.bottom - rcUI.top;
    const dxInf = Math.trunc((cxUI * (this.nScale_ - 1.0)) / 2);
    const dyInf = Math.trunc((cyUI * (this.nScale_ - 1.0)) / 2);
    const rcUIScaled = {
      left: rcUI.left - dxInf,
      top: rcUI.top - dyInf,
      right: rcUI.right + dxInf,
      bottom: rcUI.bottom + dyInf,
    };
    const cyBase = this.rcBase_.bottom - this.rcBase_.top;
    const xNew = Math.trunc(rcUIScaled.left + (this.xOrigin_ - rcUI.left) * this.nScale_);
    const yNew = Math.trunc(
      (rcUIScaled.top + rcUIScaled.bottom - cyBase * this.nScale_) / 2 -
        this.rcBase_.top * this.nScale_ +
        this.yOrigin_ * this.nScale_,
    );
    const cxNew = Math.trunc(this.cx_ * this.nScale_);
    const cyNew = Math.trunc(this.cy_ * this.nScale_);
    return { left: xNew, top: yNew, right: xNew + cxNew, bottom: yNew + cyNew };
  }

  // sdlanimsprite.cpp:152-158
  SetScale(nScale: number): void {
    if (this.nScale_ === nScale) return;
    this.nScale_ = nScale;
    this.psprm_.Update(this);
  }

  // sdlanimsprite.cpp:160-167
  SetPosition(x: number, y: number): void {
    if (this.x_ === x && this.y_ === y) return;
    this.x_ = x;
    this.y_ = y;
    this.psprm_.Update(this);
  }

  // sdlanimsprite.cpp:169-175
  Show(fShow: boolean): void {
    if (this.fVisible_ === fShow) return;
    this.fVisible_ = fShow;
    this.psprm_.Update(this);
  }

  IsVisible(): boolean {
    return this.fVisible_;
  }

  // sdlanimsprite.cpp:177-181 — the shipped build short-circuits the timer
  // animation (`#if 1 SetScale(nScaleEnd); return;`); the dead tail (and
  // OnMessage's kidmAnimationTimer stepping) is not ported.
  SetScaleAnimation(
    _nScaleStart: number,
    nScaleEnd: number,
    _cms: number,
    _cmsRate: number,
    _fAutoDestroy: boolean,
  ): void {
    this.SetScale(nScaleEnd);
  }
}

const GRABBIE_SIZE = 10; // sdlselectionsprite.cpp:3
const GRABBIE_SIZE_HALF = GRABBIE_SIZE / 2;

/** game/sdl/sdlselectionsprite.cpp SdlSelectionSprite — the drag-select rect:
 *  state (drag rect value-copy, visibility) lives in BaseSelectionSprite
 *  (ui/fingerhandler.ts); this adds the manager registration + Draw. */
export class RenderSelectionSprite extends BaseSelectionSprite implements SpriteLike {
  private readonly psprm_: SpriteManager;

  // sdlselectionsprite.cpp:8-12 — ctor registers with the manager
  constructor(psprm: SpriteManager) {
    super();
    this.psprm_ = psprm;
    psprm.Add(this);
  }

  // sdlselectionsprite.cpp:14-16 (~SdlSelectionSprite)
  Dispose(): void {
    this.psprm_.Remove(this);
  }

  override SetDragRect(drc: import('../ui/dragrect.ts').DragRect): void {
    super.SetDragRect(drc);
    this.psprm_.Update(this); // sdlselectionsprite.cpp:18-23
  }

  override Show(fShow: boolean): void {
    const fChanged = this.IsVisible() !== fShow;
    super.Show(fShow);
    if (fChanged) this.psprm_.Update(this); // sdlselectionsprite.cpp:29-35
  }

  /** bbox of the drag rect (DragRect.GetBoundingRect), axes swapped back from the
   *  rotated space Draw renders in, inflated by the grabbie squares. Returns a
   *  reused buffer; copy to retain. */
  private readonly rcDamage_: Rect = { left: 0, top: 0, right: 0, bottom: 0 };
  DamageRect(): Rect | null {
    if (!this.IsVisible()) return null;
    const rc = this.rcDamage_;
    this.GetDragRect().GetBoundingRect(rc);
    // x/y swapped — the rect lives in the rotated space (see Draw)
    const l = rc.top;
    const t = rc.left;
    const r = rc.bottom;
    const b = rc.right;
    rc.left = l - GRABBIE_SIZE_HALF;
    rc.top = t - GRABBIE_SIZE_HALF;
    rc.right = r + GRABBIE_SIZE_HALF + 1;
    rc.bottom = b + GRABBIE_SIZE_HALF + 1;
    return rc;
  }

  // sdlselectionsprite.cpp:37-79 — 4 white edges + grabbie squares; the drag
  // rect is maintained ROTATED (x/y swapped); every coordinate clamps to the
  // target surface (the SW-renderer crash note).
  Draw(pbm: CpuDibBitmap, siz: { cx: number; cy: number }, rcClip: Rect | null): void {
    if (!this.IsVisible()) return;
    const apt = [
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
      { x: 0, y: 0 },
    ];
    this.GetDragRect().GetPoints(apt); // drc_.GetPoints(apt)
    const cxMax = siz.cx - 1;
    const cyMax = siz.cy - 1;
    // SDL_SetRenderDrawColor(renderer, 255, 255, 255, 255) — LITERAL white,
    // not the palette's kiclrWhite (sdlselectionsprite.cpp:57)
    const clrWhite = { r: 255, g: 255, b: 255 } as ReturnType<typeof GetColor>;
    // the C++ draws under the manager's SDL clip rect; the CPU port clips the
    // grabbie fills via rect intersection and accepts the (axis-aligned) lines
    // inside the clip the same way
    for (let i = 0; i < 4; i++) {
      const next = i + 1 === 4 ? 0 : i + 1;
      // x/y swapped (the rect lives in the rotated space)
      const pt1 = {
        x: clip(Math.trunc(apt[i].y), 0, cxMax),
        y: clip(Math.trunc(apt[i].x), 0, cyMax),
      };
      const pt2 = {
        x: clip(Math.trunc(apt[next].y), 0, cxMax),
        y: clip(Math.trunc(apt[next].x), 0, cyMax),
      };
      drawLineClipped(pbm, pt1.x, pt1.y, pt2.x, pt2.y, clrWhite, rcClip);
      // grabbie rect at pt1, clipped to the target + clip rect
      fillClipped(
        pbm,
        pt1.x - GRABBIE_SIZE_HALF,
        pt1.y - GRABBIE_SIZE_HALF,
        GRABBIE_SIZE,
        GRABBIE_SIZE,
        clrWhite,
        siz,
        rcClip,
      );
    }
  }
}

type Clr = ReturnType<typeof GetColor>;

function drawLineClipped(
  pbm: CpuDibBitmap,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  clr: Clr,
  rcClip: Rect | null,
): void {
  if (rcClip === null) {
    pbm.DrawLine(x1, y1, x2, y2, clr);
    return;
  }
  const glLine = (pbm as unknown as GlSpriteTarget).glDrawLineClipped;
  if (glLine !== undefined) {
    glLine.call(pbm, x1, y1, x2, y2, clr, rcClip);
    return;
  }
  // Bresenham with a per-pixel clip-rect test (SDL_RenderSetClipRect semantics)
  const dx = Math.abs(x2 - x1),
    dy = Math.abs(y2 - y1);
  const sx = x1 < x2 ? 1 : -1,
    sy = y1 < y2 ? 1 : -1;
  let err = dx - dy;
  let x = x1,
    y = y1;
  for (;;) {
    if (
      x >= rcClip.left &&
      x < rcClip.right &&
      y >= rcClip.top &&
      y < rcClip.bottom &&
      x >= 0 &&
      x < pbm.cx &&
      y >= 0 &&
      y < pbm.cy
    ) {
      const i = (y * pbm.cx + x) * 4;
      pbm.rgba[i] = clr.r;
      pbm.rgba[i + 1] = clr.g;
      pbm.rgba[i + 2] = clr.b;
      pbm.rgba[i + 3] = 255;
    }
    if (x === x2 && y === y2) break;
    const e2 = 2 * err;
    if (e2 > -dy) {
      err -= dy;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      y += sy;
    }
  }
}

function fillClipped(
  pbm: CpuDibBitmap,
  x: number,
  y: number,
  cx: number,
  cy: number,
  clr: Clr,
  siz: { cx: number; cy: number },
  rcClip: Rect | null,
): void {
  let x0 = Math.max(0, x),
    y0 = Math.max(0, y);
  let x1 = Math.min(siz.cx, x + cx),
    y1 = Math.min(siz.cy, y + cy);
  if (rcClip !== null) {
    x0 = Math.max(x0, rcClip.left);
    y0 = Math.max(y0, rcClip.top);
    x1 = Math.min(x1, rcClip.right);
    y1 = Math.min(y1, rcClip.bottom);
  }
  if (x1 <= x0 || y1 <= y0) return;
  pbm.Fill(x0, y0, x1 - x0, y1 - y0, clr);
}
